import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BookOpen, Headphones, FileText, Settings as SettingsIcon, CheckCheck } from "lucide-react";
import api from "@/lib/api";

const KIND_ICON = {
    ebook: BookOpen,
    audio: Headphones,
    pdf: FileText,
    new_content: Bell,
};

function pickIcon(notif) {
    const t = (notif?.title || "").toLowerCase();
    if (t.includes("ebook") || t.includes("livre")) return BookOpen;
    if (t.includes("audio") || t.includes("cours")) return Headphones;
    if (t.includes("document") || t.includes("pdf")) return FileText;
    return Bell;
}

function timeAgo(iso) {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.round(diff / 60000);
    if (m < 1) return "à l'instant";
    if (m < 60) return `il y a ${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `il y a ${h} h`;
    const d = Math.round(h / 24);
    if (d === 1) return "hier";
    if (d < 7) return `il y a ${d} j`;
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function NotificationBell({ isDark = false }) {
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);

    // Initial unread count + lightweight polling every 60s.
    useEffect(() => {
        let mounted = true;
        const fetchCount = () =>
            api.get("/me/notifications/unread-count")
                .then((r) => { if (mounted) setUnread(r.data?.count || 0); })
                .catch(() => {});
        fetchCount();
        const id = setInterval(fetchCount, 60000);
        return () => { mounted = false; clearInterval(id); };
    }, []);

    // Load notifications when the panel opens.
    const openPanel = () => {
        setOpen(true);
        setLoading(true);
        api.get("/me/notifications?limit=20")
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const markAllRead = async () => {
        try {
            await api.post("/me/notifications/read-all");
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnread(0);
        } catch { /* ignore */ }
    };

    const onItemClick = (n) => {
        // Optimistic local read, fire-and-forget server update.
        if (!n.read) {
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
            setUnread((c) => Math.max(0, c - 1));
            api.post(`/me/notifications/${n.id}/read`).catch(() => {});
        }
        setOpen(false);
    };

    const triggerStyle = isDark
        ? { color: "rgba(244,238,223,0.92)" }
        : { color: "var(--text-primary)" };

    return (
        <div ref={wrapperRef} className="relative">
            <button
                type="button"
                onClick={() => (open ? setOpen(false) : openPanel())}
                className="relative w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-black/5"
                style={triggerStyle}
                aria-label="Notifications"
                data-testid="notif-bell"
            >
                <Bell className="w-5 h-5" strokeWidth={1.8} />
                {unread > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                        style={{
                            background: "var(--gold-500)",
                            color: "#1A1208",
                            border: "2px solid var(--bg-main, #fff)",
                        }}
                        data-testid="notif-unread-badge"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl border bg-white z-50"
                    style={{ borderColor: "var(--border-light)" }}
                    data-testid="notif-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border-light)" }}>
                        <div className="font-display text-lg" style={{ color: "var(--text-primary)" }}>Notifications</div>
                        {unread > 0 && (
                            <button
                                type="button"
                                onClick={markAllRead}
                                className="text-xs font-medium inline-flex items-center gap-1"
                                style={{ color: "var(--gold-700)" }}
                                data-testid="notif-mark-all-read"
                            >
                                <CheckCheck className="w-3.5 h-3.5" /> Tout marquer comme lu
                            </button>
                        )}
                    </div>

                    <div className="max-h-[420px] overflow-y-auto">
                        {loading ? (
                            <div className="py-10 text-center text-sm" style={{ color: "var(--text-tertiary)" }}>
                                Chargement…
                            </div>
                        ) : items.length === 0 ? (
                            <div className="py-10 px-6 text-center" data-testid="notif-empty">
                                <Bell className="w-7 h-7 mx-auto mb-3" style={{ color: "var(--gold-500)" }} strokeWidth={1.4} />
                                <div className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
                                    Aucune notification pour le moment
                                </div>
                                <div className="text-xs mt-1" style={{ color: "var(--text-tertiary)" }}>
                                    Vous serez prévenu·e dès qu'un nouveau contenu sortira.
                                </div>
                            </div>
                        ) : (
                            <ul>
                                {items.map((n) => {
                                    const Icon = pickIcon(n);
                                    return (
                                        <li key={n.id}>
                                            <Link
                                                to={n.product_id ? `/produit/${n.product_id}` : "/notifications"}
                                                onClick={() => onItemClick(n)}
                                                className="flex gap-3 px-4 py-3 transition-colors hover:bg-[#FAF6EC] border-b last:border-b-0"
                                                style={{
                                                    borderColor: "var(--border-light)",
                                                    background: n.read ? "transparent" : "rgba(201,164,88,0.06)",
                                                }}
                                                data-testid={`notif-item-${n.id}`}
                                            >
                                                <div
                                                    className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center"
                                                    style={{ background: "rgba(201,164,88,0.14)" }}
                                                >
                                                    <Icon className="w-4 h-4" style={{ color: "var(--gold-700)" }} />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium leading-snug" style={{ color: "var(--text-primary)" }}>
                                                        {n.title}
                                                    </div>
                                                    {n.body && (
                                                        <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--text-secondary)" }}>
                                                            {n.body}
                                                        </div>
                                                    )}
                                                    <div className="text-[11px] mt-1" style={{ color: "var(--text-tertiary)" }}>
                                                        {timeAgo(n.created_at)}
                                                    </div>
                                                </div>
                                                {!n.read && (
                                                    <span
                                                        aria-hidden
                                                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                                                        style={{ background: "var(--gold-500)" }}
                                                    />
                                                )}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div className="border-t flex items-center justify-between px-4 py-3 text-xs" style={{ borderColor: "var(--border-light)" }}>
                        <Link
                            to="/account#notifications"
                            onClick={() => setOpen(false)}
                            className="inline-flex items-center gap-1.5"
                            style={{ color: "var(--text-secondary)" }}
                            data-testid="notif-prefs-link"
                        >
                            <SettingsIcon className="w-3.5 h-3.5" /> Préférences
                        </Link>
                        <Link
                            to="/notifications"
                            onClick={() => setOpen(false)}
                            className="font-medium"
                            style={{ color: "var(--gold-700)" }}
                            data-testid="notif-see-all"
                        >
                            Tout voir →
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}

export { KIND_ICON };
